import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/services/adminApi";
import type {
  SlideGenerationTraceListItem,
  SlideGenerationTraceStatus,
} from "@/types/admin";
import { RefreshCcw, Filter, ExternalLink } from "lucide-react";

const PAGE_SIZE = 50;

function formatDuration(ms: number | null): string {
  if (ms == null) return "-";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatCost(cost: string | null): string {
  if (cost == null) return "-";
  const n = Number(cost);
  if (Number.isNaN(n)) return cost;
  return `$${n.toFixed(4)}`;
}

function formatTokens(tokens: number | null): string {
  if (tokens == null) return "-";
  return tokens.toLocaleString();
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

function truncate(str: string, n: number): string {
  if (str.length <= n) return str;
  return `${str.slice(0, n)}…`;
}

function statusBadge(status: SlideGenerationTraceStatus) {
  switch (status) {
    case "COMPLETED":
      return <Badge className="bg-green-100 text-green-800">completed</Badge>;
    case "FAILED":
      return <Badge className="bg-red-100 text-red-800">failed</Badge>;
    case "CANCELLED":
      return <Badge className="bg-gray-100 text-gray-800">cancelled</Badge>;
    case "IN_PROGRESS":
      return <Badge className="bg-blue-100 text-blue-800">in progress</Badge>;
    default:
      return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
  }
}

export default function SlideTracesPage() {
  const [traces, setTraces] = useState<SlideGenerationTraceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [status, setStatus] = useState<SlideGenerationTraceStatus | "">("");
  const [userId, setUserId] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getSlideTraces({
        status: status || undefined,
        userId: userId || undefined,
        organizationId: organizationId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setTraces(res.traces);
      setTotalPages(res.totalPages);
      setTotal(res.total);
    } catch (err) {
      console.error("Failed to fetch slide traces:", err);
    } finally {
      setLoading(false);
    }
  }, [page, status, userId, organizationId, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Slide Traces</h1>
          <p className="text-sm text-gray-500 mt-1">
            Observability for slide generation jobs — theme, planning,
            confirmation, and slides phases
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as SlideGenerationTraceStatus | "");
              setPage(1);
            }}
            className="text-sm border rounded px-2 py-1.5"
          >
            <option value="">All statuses</option>
            <option value="IN_PROGRESS">In progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="FAILED">Failed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
        <input
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPage(1);
          }}
          className="text-sm border rounded px-2 py-1.5"
          placeholder="Start date"
        />
        <span className="text-sm text-gray-400">→</span>
        <input
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPage(1);
          }}
          className="text-sm border rounded px-2 py-1.5"
          placeholder="End date"
        />
        <input
          type="text"
          placeholder="Filter by userId..."
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            setPage(1);
          }}
          className="text-sm border rounded px-2 py-1.5 w-56"
        />
        <input
          type="text"
          placeholder="Filter by organizationId..."
          value={organizationId}
          onChange={(e) => {
            setOrganizationId(e.target.value);
            setPage(1);
          }}
          className="text-sm border rounded px-2 py-1.5 w-56"
        />
        <span className="text-sm text-gray-400">{total} results</span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Topic</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Slides</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Cost</TableHead>
                <TableHead>Tokens</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Org</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    <RefreshCcw className="h-5 w-5 animate-spin mx-auto text-gray-400" />
                  </TableCell>
                </TableRow>
              ) : traces.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center py-8 text-gray-400"
                  >
                    No slide traces found
                  </TableCell>
                </TableRow>
              ) : (
                traces.map((trace) => (
                  <>
                    <TableRow
                      key={trace.id}
                      className={
                        trace.status === "FAILED" ? "bg-red-50/40" : ""
                      }
                    >
                      <TableCell
                        className="text-xs text-gray-500 whitespace-nowrap"
                        title={new Date(trace.createdAt).toLocaleString()}
                      >
                        {relativeTime(trace.createdAt)}
                      </TableCell>
                      <TableCell
                        className="max-w-[260px] truncate"
                        title={trace.input?.topic}
                      >
                        <span className="text-sm">
                          {trace.input?.topic
                            ? truncate(trace.input.topic, 60)
                            : "-"}
                        </span>
                        {trace.input?.isEditMode && (
                          <Badge className="ml-2 bg-purple-100 text-purple-800 text-[10px]">
                            edit
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{statusBadge(trace.status)}</TableCell>
                      <TableCell className="text-sm">
                        {trace.input?.slideCount ?? "-"}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-gray-600">
                        {formatDuration(trace.totalDurationMs)}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-gray-600">
                        {formatCost(trace.totalCostUsd)}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-gray-600">
                        {formatTokens(trace.totalTokens)}
                      </TableCell>
                      <TableCell
                        className="text-xs font-mono text-gray-500 max-w-[120px] truncate cursor-pointer hover:text-blue-600"
                        title={trace.userId}
                        onClick={() =>
                          navigator.clipboard.writeText(trace.userId)
                        }
                      >
                        {trace.userId}
                      </TableCell>
                      <TableCell
                        className="text-xs font-mono text-gray-500 max-w-[120px] truncate cursor-pointer hover:text-blue-600"
                        title={trace.organizationId}
                        onClick={() =>
                          navigator.clipboard.writeText(trace.organizationId)
                        }
                      >
                        {trace.organizationId}
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/admin/slide-traces/${trace.id}`}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                        >
                          View <ExternalLink className="h-3 w-3" />
                        </Link>
                      </TableCell>
                    </TableRow>
                    {trace.errorMessage && (
                      <TableRow key={`${trace.id}-error`}>
                        <TableCell
                          colSpan={10}
                          className="bg-red-50/60 text-xs text-red-700 font-mono py-2"
                        >
                          <span className="text-red-400">error: </span>
                          {trace.errorMessage}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
